<template>
  <div>
    <fieldset class="row">
      <legend class="col-sm-12 mb-0">
        <h1 class="h3">You&rsquo;re almost there&mldr;</h1>
        <p class="label"><sup>*</sup> required</p>
      </legend>
      <b-col md="4">
        <ValidationProvider mode="eager" rules="required" name="address" v-slot="{ errors }" slim>
          <b-form-group id="address-group" label="Address*" label-for="address">
            <b-form-input
              :state="errors[0] ? false : null"
              v-model="address"
              autocomplete="23oihl2i3iou2523iu5"
              name="main_address"
              id="address"
              aria-describedby="address-error"
              ref="address"
              @keyup.down="focusOnList"
              v-remove-aria-required
              aria-label="Address, filling out this input will auto fill city, state, and zip"
              class="btn-double-outline btn-double-outline--normal"
            >
            </b-form-input>
            <b-form-invalid-feedback id="address-error" v-if="errors.length">
              Error: Address is required
            </b-form-invalid-feedback>
            <AddressList :addresses="lookup_results" @setAddressInfo="setAddressInfo"/>
          </b-form-group>
        </ValidationProvider>
      </b-col>
      <b-col md="4">
        <ValidationProvider mode="eager" rules="required" name="cotu" v-slot="{ errors }" slim>
          <b-form-group id="city-group" label="City*" label-for="city">
            <b-form-input
              :state="errors[0] ? false : null"
              v-model="city"
              id="city"
              aria-describedby="city-error"
              readonly
              v-remove-aria-required
              role="alert"
              class="btn-double-outline btn-double-outline--normal"
            >
            </b-form-input>
            <b-form-invalid-feedback id="city-error" v-if="errors.length">
              Error: Please select an Address
            </b-form-invalid-feedback>
          </b-form-group>
        </ValidationProvider>
      </b-col>
      <b-col md="4">
        <ValidationProvider rules="required" name="select-state" v-slot="{ errors }" slim>
          <b-form-group id="state-group" label="State*" label-for="state-list">
            <b-form-select
              :state="errors[0] ? false : null"
              :options="options"
              readonly
              aria-disabled="true"
              v-model="selected_state"
              id="state-list"
              aria-describedby="state-error"
              class="btn-double-outline btn-double-outline--normal"
            />
            <b-form-invalid-feedback id="state-error" v-if="errors.length">
              Error: Please select an Address
            </b-form-invalid-feedback>
          </b-form-group>
        </ValidationProvider>
        <div role="alert" class="sr-only">{{selectedStateText}}</div>
      </b-col>
      <b-col md="4">
        <!-- https://stackoverflow.com/questions/2577236/regex-for-zip-code -->
        <ValidationProvider
          :rules="{ required: true, regex: /^\d{5}(?:[-\s]\d{4})?$/ }"
          mode="eager"
          name="zipcode"
          v-slot="{ errors }"
          slim
        >
          <b-form-group id="zipcode-group" label="Zipcode*" label-for="zipcode">
            <b-form-input
              :state="errors[0] ? false : null"
              v-model="zip_code"
              readonly
              id="zipcode"
              aria-describedby="zipcode-error"
              v-remove-aria-required
              role="alert"
              class="btn-double-outline btn-double-outline--normal"
            ></b-form-input>
            <b-form-invalid-feedback id="zipcode-error" v-if="errors.length">
              Error: Please select an Address
            </b-form-invalid-feedback>
          </b-form-group>
        </ValidationProvider>
      </b-col>
      <b-col md="4">
         <!-- https://stackoverflow.com/questions/16699007/regular-expression-to-match-standard-10-digit-phone-number -->
        <ValidationProvider
          :rules="{ required: true, regex: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/ }"
          mode="eager"
          name="phone"
          v-slot="{ errors }"
          slim
        >
        <b-form-group id="phone-group" label="Phone*" label-for="phone">
          <b-form-input
            :state="errors[0] ? false : null"
            v-model="phone"
            id="phone"
            aria-describedby="phone-error"
            type="tel"
            v-mask="'(###) ###-####'"
            v-remove-aria-required
            class="btn-double-outline btn-double-outline--normal"
            autocomplete="tel"
          ></b-form-input>
          <b-form-invalid-feedback id="phone-error" v-if="errors.length">
              Error: Phone is required
            </b-form-invalid-feedback>
        </b-form-group>
        </ValidationProvider>
      </b-col>
    </fieldset>
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce';
import AddressList from '@/components/AddressList.vue';

const SmartyStreetsSDK = require('smartystreets-javascript-sdk');

const SmartyStreetsCore = SmartyStreetsSDK.core;
const { Lookup } = SmartyStreetsSDK.usAutocompletePro;

const key = process.env.VUE_APP_SMARTY_STREETS_KEY;
const credentials = new SmartyStreetsCore.SharedCredentials(key);
const clientBuilder = new SmartyStreetsCore.ClientBuilder(credentials).withBaseUrl(process.env.VUE_APP_SMARTY_BASE_URL).withLicenses([process.env.VUE_APP_SMARTY_STREETS_LICENSE]);
const client = clientBuilder.buildUsAutocompleteProClient();


export default {
  name: 'ContactInfo',
  components: {
    AddressList,
  },
  data() {
    return {
      lookup_results: [],
      is_lookup_enabled: false,
      options: [{ value: null, text: 'State', disabled: true }, { text: 'Alabama', value: 'AL' }, { text: 'Alaska', value: 'AK' }, { text: 'American Samoa', value: 'AS' }, { text: 'Arizona', value: 'AZ' }, { text: 'Arkansas', value: 'AR' }, { text: 'California', value: 'CA' }, { text: 'Colorado', value: 'CO' }, { text: 'Connecticut', value: 'CT' }, { text: 'Delaware', value: 'DE' }, { text: 'District Of Columbia', value: 'DC' }, { text: 'Federated States Of Micronesia', value: 'FM' }, { text: 'Florida', value: 'FL' }, { text: 'Georgia', value: 'GA' }, { text: 'Guam', value: 'GU' }, { text: 'Hawaii', value: 'HI' }, { text: 'Idaho', value: 'ID' }, { text: 'Illinois', value: 'IL' }, { text: 'Indiana', value: 'IN' }, { text: 'Iowa', value: 'IA' }, { text: 'Kansas', value: 'KS' }, { text: 'Kentucky', value: 'KY' }, { text: 'Louisiana', value: 'LA' }, { text: 'Maine', value: 'ME' }, { text: 'Marshall Islands', value: 'MH' }, { text: 'Maryland', value: 'MD' }, { text: 'Massachusetts', value: 'MA' }, { text: 'Michigan', value: 'MI' }, { text: 'Minnesota', value: 'MN' }, { text: 'Mississippi', value: 'MS' }, { text: 'Missouri', value: 'MO' }, { text: 'Montana', value: 'MT' }, { text: 'Nebraska', value: 'NE' }, { text: 'Nevada', value: 'NV' }, { text: 'New Hampshire', value: 'NH' }, { text: 'New Jersey', value: 'NJ' }, { text: 'New Mexico', value: 'NM' }, { text: 'New York', value: 'NY' }, { text: 'North Carolina', value: 'NC' }, { text: 'North Dakota', value: 'ND' }, { text: 'Northern Mariana Islands', value: 'MP' }, { text: 'Ohio', value: 'OH' }, { text: 'Oklahoma', value: 'OK' }, { text: 'Oregon', value: 'OR' }, { text: 'Palau', value: 'PW' }, { text: 'Pennsylvania', value: 'PA' }, { text: 'Puerto Rico', value: 'PR' }, { text: 'Rhode Island', value: 'RI' }, { text: 'South Carolina', value: 'SC' }, { text: 'South Dakota', value: 'SD' }, { text: 'Tennessee', value: 'TN' }, { text: 'Texas', value: 'TX' }, { text: 'Utah', value: 'UT' }, { text: 'Vermont', value: 'VT' }, { text: 'Virgin Islands', value: 'VI' }, { text: 'Virginia', value: 'VA' }, { text: 'Washington', value: 'WA' }, { text: 'West Virginia', value: 'WV' }, { text: 'Wisconsin', value: 'WI' }, { text: 'Wyoming', value: 'WY' }],
    };
  },
  watch: {
    address(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== '') {
        this.validateStep();
      }
    },
    city(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== '') {
        this.validateStep();
      }
    },
    selected_state(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== '') {
        this.validateStep();
      }
    },
    zip_code(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== '') {
        this.validateStep();
      }
    },
    phone(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== '') {
        this.validateStep();
      }
    },
    lookup_results(newVal, oldVal) {
      if (newVal === oldVal) { return; }

      this.is_lookup_enabled = (newVal.length > 0);
    },
  },
  computed: {
    selectedStateText() {
      if (this.selected_state === null) {
        return '';
      }
      const stateValues = this.options.find(item => item.value === this.selected_state);
      return stateValues ? stateValues.text : '';
    },
    address: {
      get() {
        return this.$store.state.AddressLine1;
      },
      set(value) {
        this.$store.dispatch('addressLine1', value);
      },
    },
    city: {
      get() {
        return this.$store.state.City;
      },
      set(value) {
        this.$store.dispatch('city', value);
      },
    },
    selected_state: {
      get() {
        return this.$store.state.State;
      },
      set(value) {
        this.$store.dispatch('state', value);
      },
    },
    zip_code: {
      get() {
        return this.$store.state.ZipCode;
      },
      set(value) {
        this.$store.dispatch('zipCode', value);
      },
    },
    phone: {
      get() {
        return this.$store.state.Phone;
      },
      set(value) {
        this.$store.dispatch('phone', value);
      },
    },
  },
  mounted() {
    this.$refs.address.$el.addEventListener('keydown', debounce(this.lookupSmartyStreets, 450));
  },
  methods: {
    validateStep() {
      this.$nextTick(() => this.$emit('onValidate', null));
    },
    async lookupSmartyStreets(event, selected = '') {
      if (this.address.length < 3) { return; }

      try {
        const myLookup = new Lookup(this.address);

        if (selected !== '') {
          myLookup.selected = selected;
        }

        const response = await client.send(myLookup);
        this.lookup_results = response.result;
      } catch (error) {
        console.log(error);
      }
    },
    setAddressInfo(address, selected) {
      // console.log('!! setAddress:', address, selected);
      if (address.entries && address.entries > 1) {
        this.lookupSmartyStreets(null, selected);
        return;
      }

      const addressValue = address.secondary
        ? `${address.streetLine} ${address.secondary}`
        : address.streetLine;

      this.$store.dispatch('addressLine1', addressValue);
      this.$store.dispatch('city', address.city);
      this.$store.dispatch('state', address.state);
      this.$store.dispatch('zipCode', address.zipcode);
      this.lookup_results = [];
    },
    focusOnList(event) {
      if (this.is_lookup_enabled) {
        event.preventDefault();
        event.stopPropagation();
        const addressList = document.getElementById('AddressListMenu');
        addressList.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_breakpoints.scss';

.form-group {
  margin-bottom: 57px;
  position: relative;

  @include media-breakpoint-down(md) {
    margin-bottom: 16px;
  }
}

h1 {
  margin-bottom: 36px;
}
select[readonly] {
  background-color: rgba(233, 236, 239, 0.3);
  opacity: 1;
  pointer-events: none;
  touch-action: none;
}
</style>
